// Etapa 3: Assinatura

import React, { useRef, useEffect, useState, useCallback } from "react";
import { HiPencil, HiUpload, HiX } from "react-icons/hi";
import { DadosAssinatura } from "../../../types/orcamento-fornecedor.types";

interface Etapa3Props {
  dadosAssinatura: Partial<DadosAssinatura>;
  onDadosChange: (dados: Partial<DadosAssinatura>) => void;
  errors: Record<string, string>;
  showErrors?: boolean;
}

export const Etapa3Assinatura: React.FC<Etapa3Props> = ({
  dadosAssinatura,
  onDadosChange,
  errors,
  showErrors = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasDigitadaRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>("");
  const [nomeDigitado, setNomeDigitado] = useState("");
  const [fonteSelecionada, setFonteSelecionada] = useState("Great Vibes");

  const fontesDisponiveis = [
    { nome: "Elegante", familia: "Great Vibes" },
    { nome: "Moderna", familia: "Dancing Script" },
    { nome: "Clássica", familia: "Sacramento" },
    { nome: "Robusta", familia: "Pacifico" },
  ];

  const tipoAssinatura = dadosAssinatura.tipo || "manual";
  const assinaturaData = dadosAssinatura.assinaturaData || "";

  const handleTipoChange = (novoTipo: "manual" | "upload" | "digitada") => {
    if (novoTipo === tipoAssinatura) return;

    const temAssinatura = assinaturaData.trim().length > 0;

    if (temAssinatura) {
      const confirmou = window.confirm(
        "Ao trocar o tipo de assinatura, a assinatura atual será perdida. Deseja continuar?",
      );
      if (!confirmou) return;
    }

    // Limpar canvas manual se existir
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    setPreviewImage("");
    setNomeDigitado("");
    onDadosChange({ tipo: novoTipo, assinaturaData: "" });
  };

  // Computado diretamente — sem useEffect para evitar race condition no mount
  const isExternalSignature =
    assinaturaData.startsWith("http://") ||
    assinaturaData.startsWith("https://");

  const getCoordinates = (
    e:
      | React.MouseEvent<HTMLCanvasElement>
      | React.TouchEvent<HTMLCanvasElement>,
    canvas: HTMLCanvasElement,
  ) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if ("touches" in e) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (
    e:
      | React.MouseEvent<HTMLCanvasElement>
      | React.TouchEvent<HTMLCanvasElement>,
  ) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const { x, y } = getCoordinates(e, canvas);

    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (
    e:
      | React.MouseEvent<HTMLCanvasElement>
      | React.TouchEvent<HTMLCanvasElement>,
  ) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const { x, y } = getCoordinates(e, canvas);

    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing && canvasRef.current) {
      try {
        const dataURL = canvasRef.current.toDataURL();
        onDadosChange({ assinaturaData: dataURL });
      } catch (error) {
        console.error("Erro ao exportar canvas:", error);
        // Se o canvas está tainted, limpar e permitir novo desenho
        limparAssinatura();
      }
    }
    setIsDrawing(false);
  };

  const limparAssinatura = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }

    // Sempre limpar os dados, independente do canvas
    setPreviewImage("");
    onDadosChange({ assinaturaData: "" });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Por favor, selecione apenas arquivos de imagem");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert("O arquivo deve ter no máximo 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataURL = event.target?.result as string;
      setPreviewImage(dataURL);
      onDadosChange({ assinaturaData: dataURL });
    };
    reader.readAsDataURL(file);
  };

  const removerUpload = () => {
    setPreviewImage("");
    onDadosChange({ assinaturaData: "" });
  };

  useEffect(() => {
    if (tipoAssinatura === "manual" && canvasRef.current) {
      const ctx = canvasRef.current.getContext("2d");
      if (ctx) {
        ctx.strokeStyle = "#000";
        ctx.lineWidth = 2;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
      }
    }
  }, [tipoAssinatura]);

  // Carregar Google Fonts para assinatura digitada
  useEffect(() => {
    const id = "google-fonts-assinatura";
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Great+Vibes&family=Dancing+Script:wght@600&family=Sacramento&family=Pacifico&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  // Renderizar preview da assinatura digitada no canvas
  const renderizarAssinaturaDigitada = useCallback(() => {
    const canvas = canvasDigitadaRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    if (!nomeDigitado) return;
    ctx.fillStyle = "#111";
    ctx.font = `72px "${fonteSelecionada}"`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(nomeDigitado, canvas.width / 2, canvas.height / 2);
  }, [nomeDigitado, fonteSelecionada]);

  // Aplicar assinatura automaticamente quando nome ou fonte mudarem
  useEffect(() => {
    if (tipoAssinatura !== "digitada") return;
    if (!nomeDigitado.trim()) {
      // Não limpar se já existe uma assinatura salva externamente
      const isExternal =
        assinaturaData.startsWith("http://") ||
        assinaturaData.startsWith("https://");
      if (!isExternal) {
        onDadosChange({ assinaturaData: "" });
      }
      return;
    }

    document.fonts.ready.then(() => {
      renderizarAssinaturaDigitada();
      setTimeout(() => {
        const canvas = canvasDigitadaRef.current;
        if (canvas) {
          const dataURL = canvas.toDataURL();
          onDadosChange({ assinaturaData: dataURL });
        }
      }, 100);
    });
  }, [
    nomeDigitado,
    fonteSelecionada,
    tipoAssinatura,
    assinaturaData,
    renderizarAssinaturaDigitada,
    onDadosChange,
  ]);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
          <div className="w-10 h-10 rounded-lg bg-[#4760e0]/10 flex items-center justify-center">
            <HiPencil className="h-6 w-6 text-[#4760e0]" />
          </div>
          <h2 className="text-xl font-bold text-[#161519]">
            Assinatura do orçamento
          </h2>
        </div>

        <div className="flex gap-2 mb-6 bg-gray-100 p-1 rounded-lg">
          <button
            type="button"
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-medium transition-all ${
              tipoAssinatura === "manual"
                ? "bg-[#4760e0] text-white shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
            onClick={() => handleTipoChange("manual")}
          >
            <HiPencil className="h-5 w-5" />
            Assinatura Manual
          </button>
          <button
            type="button"
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-medium transition-all ${
              tipoAssinatura === "upload"
                ? "bg-[#4760e0] text-white shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
            onClick={() => handleTipoChange("upload")}
          >
            <HiUpload className="h-5 w-5" />
            Upload de Imagem
          </button>
          <button
            type="button"
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-medium transition-all ${
              tipoAssinatura === "digitada"
                ? "bg-[#4760e0] text-white shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
            onClick={() => handleTipoChange("digitada")}
          >
            <HiPencil className="h-5 w-5" />
            Nome Digitado
          </button>
        </div>

        {tipoAssinatura === "manual" ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {isExternalSignature
                ? "Esta assinatura já foi salva. Para alterá-la, limpe o campo e desenhe uma nova assinatura."
                : "Desenhe sua assinatura usando o mouse ou toque na tela"}
            </p>

            {isExternalSignature ? (
              // Mostrar preview da assinatura externa (não editável)
              <div className="space-y-4">
                <div className="border-2 border-gray-200 rounded-lg p-6 bg-gray-50">
                  <img
                    src={assinaturaData}
                    alt="Assinatura salva"
                    className="max-h-32 mx-auto"
                  />
                </div>
                <button
                  type="button"
                  onClick={limparAssinatura}
                  className="px-4 py-2 rounded-lg border-2 border-gray-300 text-gray-700 font-medium hover:border-red-500 hover:text-red-600 transition-all flex items-center gap-2"
                >
                  <HiX className="h-5 w-5" />
                  Limpar e Criar Nova Assinatura
                </button>
              </div>
            ) : (
              // Canvas para desenhar nova assinatura
              <>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-2 bg-white">
                  <canvas
                    ref={canvasRef}
                    width={700}
                    height={200}
                    className="w-full cursor-crosshair touch-none"
                    style={{ maxWidth: "100%" }}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                  />
                </div>
                <button
                  type="button"
                  onClick={limparAssinatura}
                  className="px-4 py-2 rounded-lg border-2 border-gray-300 text-gray-700 font-medium hover:border-red-500 hover:text-red-600 transition-all flex items-center gap-2"
                >
                  <HiX className="h-5 w-5" />
                  Limpar Assinatura
                </button>
              </>
            )}
          </div>
        ) : tipoAssinatura === "digitada" ? (
          isExternalSignature ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Esta assinatura já foi salva. Para alterá-la, clique em "Alterar
                assinatura".
              </p>
              <div className="border-2 border-gray-200 rounded-lg p-6 bg-gray-50">
                <img
                  src={assinaturaData}
                  alt="Assinatura salva"
                  className="max-h-32 mx-auto"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  onDadosChange({ assinaturaData: "" });
                }}
                className="px-4 py-2 rounded-lg border-2 border-gray-300 text-gray-700 font-medium hover:border-red-500 hover:text-red-600 transition-all flex items-center gap-2"
              >
                <HiX className="h-5 w-5" />
                Alterar assinatura
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Digite seu nome e escolha uma fonte cursiva para gerar a
                assinatura
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome para a assinatura
                </label>
                <input
                  type="text"
                  value={nomeDigitado}
                  onChange={(e) => setNomeDigitado(e.target.value)}
                  placeholder="Digite seu nome completo"
                  className="w-full px-4 py-2.5 rounded-lg border-2 border-gray-200 focus:border-[#4760e0] focus:ring-2 focus:ring-[#4760e0]/20 outline-none transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estilo da fonte
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {fontesDisponiveis.map((fonte) => (
                    <button
                      key={fonte.familia}
                      type="button"
                      className={`px-4 py-3 rounded-lg border-2 transition-all text-center ${
                        fonteSelecionada === fonte.familia
                          ? "border-[#4760e0] bg-[#4760e0]/5"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      onClick={() => setFonteSelecionada(fonte.familia)}
                    >
                      <span
                        style={{
                          fontFamily: fonte.familia,
                          fontSize: "1.4rem",
                        }}
                      >
                        {nomeDigitado || fonte.nome}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-2 bg-white">
                <canvas
                  ref={canvasDigitadaRef}
                  width={700}
                  height={150}
                  className="w-full"
                  style={{ maxWidth: "100%" }}
                />
              </div>
              {assinaturaData &&
                assinaturaData.startsWith("data:image/") &&
                tipoAssinatura === "digitada" &&
                nomeDigitado.trim() && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <span className="text-sm text-green-700 font-medium">
                      ✓ Assinatura aplicada automaticamente
                    </span>
                    <button
                      type="button"
                      onClick={() => onDadosChange({ assinaturaData: "" })}
                      className="ml-auto text-sm text-red-500 hover:text-red-700"
                    >
                      Limpar
                    </button>
                  </div>
                )}
            </div>
          )
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Faça upload de uma imagem com sua assinatura (PNG, JPG, JPEG -
              máx. 5MB)
            </p>
            {previewImage || assinaturaData ? (
              <div className="space-y-4">
                <div className="border-2 border-gray-200 rounded-lg p-6 bg-gray-50">
                  <img
                    src={previewImage || assinaturaData}
                    alt="Assinatura"
                    className="max-h-32 mx-auto"
                  />
                </div>
                <button
                  type="button"
                  onClick={removerUpload}
                  className="px-4 py-2 rounded-lg border-2 border-red-300 text-red-600 font-medium hover:bg-red-50 transition-all flex items-center gap-2"
                >
                  <HiX className="h-5 w-5" />
                  Remover
                </button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                <HiUpload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="mb-4 text-gray-700 font-medium">
                  Selecione um arquivo de imagem
                </p>
                <label className="inline-flex items-center px-6 py-3 bg-[#4760e0] text-white rounded-lg font-medium cursor-pointer hover:bg-[#3850d0] transition-all">
                  Escolher arquivo
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>
                <p className="text-xs text-gray-500 mt-4">
                  PNG, JPG, JPEG até 5MB
                </p>
              </div>
            )}
          </div>
        )}

        {showErrors && errors.assinaturaData && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600 font-medium">
              {errors.assinaturaData}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
